import React, { useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import moment from 'moment'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  AlertTitle,
  Button,
  Checkbox,
  FormControlLabel,
  Grid,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import CorporateSplitPage from '../../../../components/SplitPage/CorporateSplitPage'
import API from '../../../../services/API'

// Public witness statement form behind an external statement link
// (/witness-statement/:token). No login: the token is a 192-bit random,
// single-use secret with an expiry. The page shows only what the reviewer
// chose to share (incident number/date and a brief) plus a privacy notice.

const CLOSED_MESSAGES = {
  expired: {
    title: 'This link has expired',
    body: 'Statement links only stay open for a limited time. If you still wish to give a statement, please contact the organisation that sent you the link and they can issue a new one.',
  },
  used: {
    title: 'A statement has already been submitted',
    body: 'This link has already been used to submit a statement and cannot be used again. If you need to add to your statement, please contact the organisation directly.',
  },
  revoked: {
    title: 'This link has been withdrawn',
    body: 'The organisation has withdrawn this statement request. If you believe this is a mistake, please contact them directly.',
  },
  invalid: {
    title: "We couldn't recognise this link",
    body: 'Please check that you have opened the complete link from your email or message. If the problem continues, contact the organisation that sent it.',
  },
  throttled: {
    title: 'Too many attempts',
    body: 'Please wait a few minutes and try again.',
  },
}

const PrivacyNotice = ({ orgName, privacyContact }) => (
  <Accordion variant="outlined" sx={{ borderRadius: 2, '&:before': { display: 'none' } }}>
    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
      <Typography fontWeight={700}>Privacy notice — how your statement is used</Typography>
    </AccordionSummary>
    <AccordionDetails>
      <Stack spacing={1.25}>
        <Typography variant="body2">
          <strong>Who is collecting your information:</strong> {orgName || 'The organisation'}{' '}
          is the data controller for the information you give on this form. AmbuNet provides
          the software and processes the information on their behalf.
        </Typography>
        <Typography variant="body2">
          <strong>Why we are asking:</strong> your statement will be used to investigate and
          record the incident referenced above, to learn from it, and to meet the
          organisation's legal and regulatory duties in relation to safety, incident reporting
          and record-keeping.
        </Typography>
        <Typography variant="body2">
          <strong>Our lawful basis:</strong> the organisation has a legitimate interest in
          investigating incidents properly and, in many cases, a legal obligation to do so.
          Where your statement includes information about someone's health, it is processed
          for the purposes of health and social care and for establishing, exercising or
          defending legal claims. Giving a statement is voluntary.
        </Typography>
        <Typography variant="body2">
          <strong>Who will see it:</strong> the staff investigating and reviewing the incident.
          The statement forms part of the incident record and may be shared where the law
          requires or permits — for example with a regulator, insurer, legal adviser, coroner
          or the police.
        </Typography>
        <Typography variant="body2">
          <strong>How long it is kept:</strong> for as long as the incident record itself is
          retained under the organisation's records policy. The email address the link was sent
          to is deleted automatically once the request is closed.
        </Typography>
        <Typography variant="body2">
          <strong>Your rights:</strong> you can ask for a copy of the information held about
          you, ask for inaccuracies to be corrected, and object to or ask for restriction of
          processing. You can also complain to the Information Commissioner's Office (UK) or
          the Data Protection Commission (Ireland).
          {privacyContact && (
            <>
              {' '}
              To exercise your rights, contact <strong>{privacyContact}</strong>.
            </>
          )}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          See also the{' '}
          <Link to="/privacy-policy" target="_blank" rel="noreferrer">
            AmbuNet privacy policy
          </Link>{' '}
          for how the platform itself handles data.
        </Typography>
      </Stack>
    </AccordionDetails>
  </Accordion>
)

const WitnessStatementPublic = () => {
  const { token } = useParams()
  // 'checking' | 'closed' | 'form' | 'submitted'
  const [step, setStep] = useState('checking')
  const [state, setState] = useState(null)
  const [request, setRequest] = useState(null)

  const [witnessName, setWitnessName] = useState('')
  const [witnessContact, setWitnessContact] = useState('')
  const [witnessInvolvement, setWitnessInvolvement] = useState('')
  const [witnessStatement, setWitnessStatement] = useState('')
  const [privacyAccepted, setPrivacyAccepted] = useState(false)
  const [declarationAccepted, setDeclarationAccepted] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [problems, setProblems] = useState([])

  useEffect(() => {
    setStep('checking')
    API.get(`/witness-statement/${token}`, { skipGlobalErrorHandler: true })
      .then((res) => {
        const nextState = res.status === 200 ? res.data?.state : res.data?.state || 'invalid'
        setState(nextState)
        if (nextState === 'open') {
          setRequest(res.data)
          setWitnessName(res.data.witnessName ?? '')
          setWitnessInvolvement(res.data.witnessInvolvement ?? '')
          setStep('form')
        } else {
          setStep('closed')
        }
      })
      .catch(() => {
        setState('invalid')
        setStep('closed')
      })
  }, [token])

  const canSubmit =
    witnessName.trim() &&
    witnessContact.trim() &&
    witnessInvolvement.trim() &&
    witnessStatement.trim().length >= 20 &&
    privacyAccepted &&
    declarationAccepted &&
    !submitting

  const handleSubmit = (e) => {
    e.preventDefault()
    if (!canSubmit) return
    setSubmitting(true)
    setProblems([])
    API.post(
      `/witness-statement/${token}`,
      {
        witnessName: witnessName.trim(),
        witnessContact: witnessContact.trim(),
        witnessInvolvement: witnessInvolvement.trim(),
        witnessStatement: witnessStatement.trim(),
        privacyNoticeAccepted: privacyAccepted,
        declarationAccepted,
      },
      { skipGlobalErrorHandler: true },
    )
      .then((res) => {
        if (res.status === 200) {
          setStep('submitted')
        } else if (res.status === 400 && res.data?.problems) {
          setProblems(res.data.problems)
        } else if (res.status === 409 || res.status === 429) {
          setState(res.data?.state || 'invalid')
          setStep('closed')
        } else {
          setProblems(['Something went wrong sending your statement. Please try again.'])
        }
      })
      .catch(() =>
        setProblems(['Something went wrong sending your statement. Please try again.']),
      )
      .finally(() => setSubmitting(false))
  }

  const closed = CLOSED_MESSAGES[state] ?? CLOSED_MESSAGES.invalid

  return (
    <CorporateSplitPage pageTitle="Witness Statement">
      {step === 'checking' ? (
        <Alert severity="info" icon={false}>
          Checking your statement link…
        </Alert>
      ) : step === 'closed' ? (
        <Alert severity="warning">
          <AlertTitle>{closed.title}</AlertTitle>
          {closed.body}
        </Alert>
      ) : step === 'submitted' ? (
        <Alert severity="success">
          <AlertTitle>Thank you</AlertTitle>
          Your statement has been submitted to {request?.orgName || 'the organisation'} and
          the team reviewing the incident has been notified. This link can no longer be used.
          You may now close this page.
        </Alert>
      ) : (
        <Stack gap={3} component="form" onSubmit={handleSubmit}>
          <Alert severity="info" icon={false}>
            <Typography variant="title3">
              {request?.orgName || 'We'} have asked you to provide a witness statement.
            </Typography>
            <Typography variant="body2" sx={{ mt: 1 }}>
              Incident reference <strong>#{request?.incidentNumber}</strong>
              {request?.incidentDate &&
                ` on ${moment(request.incidentDate).format('dddd Do MMMM YYYY')}`}
              . Please describe, in your own words, what you saw and heard. Stick to facts you
              witnessed yourself rather than what you were told by others, and include times
              where you can. This link is personal to you, can be used once, and expires on{' '}
              <strong>{moment(request?.expires).format('Do MMMM YYYY [at] HH:mm')}</strong>.
            </Typography>
          </Alert>

          {request?.brief && (
            <Alert severity="info" variant="outlined" icon={false}>
              <AlertTitle>What we would like you to cover</AlertTitle>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
                {request.brief}
              </Typography>
            </Alert>
          )}

          {problems.length > 0 && (
            <Alert severity="error">
              <AlertTitle>Please check your statement</AlertTitle>
              {problems.map((problem) => (
                <div key={problem}>{problem}</div>
              ))}
            </Alert>
          )}

          <Grid container spacing={2}>
            <Grid item xs={12} lg={6}>
              <TextField
                label="Your full name"
                value={witnessName}
                onChange={(e) => setWitnessName(e.target.value)}
                required
                fullWidth
                autoComplete="name"
              />
            </Grid>
            <Grid item xs={12} lg={6}>
              <TextField
                label="Contact details (email or phone)"
                value={witnessContact}
                onChange={(e) => setWitnessContact(e.target.value)}
                required
                fullWidth
                helperText="So the investigating team can reach you if they need to clarify anything"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Your involvement"
                placeholder="e.g. Member of the public standing nearby; event steward; relative of the patient"
                value={witnessInvolvement}
                onChange={(e) => setWitnessInvolvement(e.target.value)}
                required
                fullWidth
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Your statement"
                value={witnessStatement}
                onChange={(e) => setWitnessStatement(e.target.value.slice(0, 20000))}
                required
                fullWidth
                multiline
                minRows={8}
                helperText={`${witnessStatement.trim().length} characters — please write at least a few sentences. You cannot edit the statement after submitting.`}
              />
            </Grid>
          </Grid>

          <PrivacyNotice orgName={request?.orgName} privacyContact={request?.privacyContact} />

          <Stack spacing={0.5}>
            <FormControlLabel
              sx={{ alignItems: 'flex-start', ml: 0 }}
              control={
                <Checkbox
                  checked={privacyAccepted}
                  onChange={(e) => setPrivacyAccepted(e.target.checked)}
                  sx={{ pt: 0.25 }}
                />
              }
              label={
                <Typography variant="body2">
                  I have read the privacy notice and understand how my statement and contact
                  details will be used.
                </Typography>
              }
            />
            <FormControlLabel
              sx={{ alignItems: 'flex-start', ml: 0 }}
              control={
                <Checkbox
                  checked={declarationAccepted}
                  onChange={(e) => setDeclarationAccepted(e.target.checked)}
                  sx={{ pt: 0.25 }}
                />
              }
              label={
                <Typography variant="body2">
                  I confirm that this statement is true to the best of my knowledge and belief,
                  and that I have written it myself.
                </Typography>
              }
            />
          </Stack>

          <Button
            type="submit"
            color="success"
            variant="contained"
            size="large"
            fullWidth
            disabled={!canSubmit}
          >
            {submitting ? 'Submitting…' : 'Submit my statement'}
          </Button>
        </Stack>
      )}
    </CorporateSplitPage>
  )
}

export default WitnessStatementPublic
